import React, { useDispatch, useState, useEffect, useMemo, useRef, useCallback } from 'reactn'
import { navigate } from 'gatsby'
import { confirmPayment, setLatestEmailFromLink } from '../../state/network'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import CartError from './cartError'
import {
  Box, Button, Center, VStack, Divider, Flex, useBreakpointValue, Checkbox, Text,
} from "@chakra-ui/react"
import {
  Elements, AddressElement, LinkAuthenticationElement, useElements, useStripe,
} from "@stripe/react-stripe-js"
import './checkout.css'

const styleVariables = {
  colorPrimary: '#000',
  colorBackground: '#fff',
  fontWeight: '300',
  borderRadius: '0px',
  fontSize: '14px',
  colorText: '#30313d',
  colorDanger: '#df1b41',
  colorIcon: '#000',
  colorLogo: 'light',
  fontWeightLight: '300',
  fontWeightNormal: '300',
  fontWeightMedium: '300',
  fontFamily: 'ApercuLight, Roboto, Open Sans, Segoe UI, sans-serif',
  spacingUnit: '2px',
}

import PaymentForm from './cartPayment';
import ShoppingCartTotals from './shoppingCartTotals';
// import CartUpsells from './sections/cartUpsells';
import ExpressPayment from './expressPayment';
import { Dots } from './dotsAnimation';
import { useOnlineStatus } from '../utils/onlineStatus'

const appearance = {
  theme: 'none',
  rules: {
    '.Input': {
      border: '1px solid #000',
      borderRadius: '0px',
      boxShadow: 'none',
      fontSize: '14px',
      letterSpacing: 'normal',
      padding: '10px',
      outline: 'none',
      marginBottom: '0px',
      textTransform: 'uppercase',
    },

    '.Input--empty': {
      border: '1px solid #000',
      padding: '10px',
      marginBottom: '0px',
    },

    '.AccordionItem': {
      border: '0px solid #000',
      borderRadius: '0px',
      boxShadow: 'none',
      textTransform: 'uppercase',
    },

    '.Input:focus-visible': {
      border: '1px solid #000',
      borderRadius: '0px',
      boxShadow: 'none',
      textTransform: 'uppercase',
      outline: 'none !important',
    },

    '.PickerItem': {
      border: '0px solid #000',
      borderRadius: '0px',
      paddingLeft: '0px',
      boxShadow: 'none',
      textTransform: 'uppercase',
    },

    '.PickerItem--selected': {
      border: '0px solid #000',
      borderRadius: '0px',
      marginLeft: '0px',
      boxShadow: 'none',
      textTransform: 'uppercase',
    },

    '.CodeInput': {
      border: '1px solid #000',
      borderRadius: '0px',
      boxShadow: 'none',
    },

    '.MenuAction': {
      textTransform: 'uppercase',
      fontSize: '12px',
      borderRadius: '0px',
    },

    '.PickerAction': {
      textTransform: 'uppercase',
      fontSize: '12px',
      borderRadius: '0px',
    },

    '.Block': {
      border: '1px solid #000',
      borderRadius: '0px',
      marginLeft: '0px',
      boxShadow: 'none',
    },

    '.CheckboxInput': {
      border: '1px solid #000',
      borderRadius: '0px',
      boxShadow: 'none',
    },

    '.Input::placeholder': {
      color: 'red',
      opacity: '0.5'
    },
    '.Input:hover': {
      color: 'var(--colorText)'
    },

    '.Input--selected': {
      borderColor: '#000',
      borderRadius: '0px',
      boxShadow: 'none',
    },

    '.Accordion:focus-visible': {
      borderColor: '#000',
      borderRadius: '0px',
      boxShadow: 'none',
    },

    '.Label': {
      textTransform: 'uppercase',
      fontSize: '14px'
    },

    '.TabLabel': {
      textTransform: 'uppercase',
      fontSize: '14px'
    },

    '.Tab': {
      border: '1px solid #000',
      borderRadius: '0px',
      boxShadow: 'none',
    },

    '.Tab:focus': {
      border: '2px solid #000',
      boxShadow: 'none',
    },

    '.Tab--selected:focus': {
      border: '2px solid #000',
      boxShadow: 'none',
    },

    '.Input--invalid': {
      boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.07), 0 0 0 2px var(--colorDanger)',
    },
  },
  variables: styleVariables
};

// Component inside Elements - handles ConfirmationToken creation
// This must be inside Elements to access fresh elements that include PaymentElement
const CheckoutContent = ({ globalState }) => {
  const { store, updateGlobal } = globalState
  const stripe = useStripe()
  const elements = useElements()
  const lastElementsRef = useRef(null)
  const { createTokenRequest } = store

  // Store Stripe and Elements in global state - update when elements change
  useEffect(() => {
    if (stripe && elements && elements !== lastElementsRef.current) {
      lastElementsRef.current = elements

      // Inspect all available elements
      const availableElements = []
      const elementTypes = ['payment', 'address', 'linkAuthentication', 'expressCheckout']

      elementTypes.forEach(type => {
        try {
          const element = elements.getElement(type)
          if (element) {
            availableElements.push(type)
            console.log(`[ELEMENTS] Found ${type} element:`, element)
          }
        } catch (e) {
          // Element type doesn't exist or not available
        }
      })

      console.log('[ELEMENTS] Available elements:', availableElements)
      console.log('[ELEMENTS] Full elements object:', elements)

      updateGlobal({
        stripeForConfirmation: stripe,
        elementsForConfirmation: elements
      })
    }
  }, [stripe, elements]) // eslint-disable-line react-hooks/exhaustive-deps

  // TEMPORARILY DISABLED: Handle ConfirmationToken creation when requested
  // Re-enable this when we want to use confirmation tokens again
  // useEffect(() => {
  //   if (!createTokenRequest) {
  //     console.log('[CONFIRMATION TOKEN] No createTokenRequest')
  //     return
  //   }
  //
  //   if (!stripe) {
  //     console.error('[CONFIRMATION TOKEN] ERROR: No stripe instance!')
  //     return
  //   }
  //
  //   if (!elements) {
  //     console.error('[CONFIRMATION TOKEN] ERROR: No elements instance!')
  //     return
  //   }

  //   console.log('[CONFIRMATION TOKEN] Starting token creation...')
  //   const { onComplete } = createTokenRequest

  //   // Verify PaymentElement is available
  //   const paymentElement = elements.getElement('payment')
  //   if (!paymentElement) {
  //     console.error('[CONFIRMATION TOKEN] ERROR: PaymentElement not found in elements!')
  //     console.error('[CONFIRMATION TOKEN] Available elements:', {
  //       payment: !!elements.getElement('payment'),
  //       address: !!elements.getElement('address'),
  //       linkAuthentication: !!elements.getElement('linkAuthentication'),
  //       expressCheckout: !!elements.getElement('expressCheckout')
  //     })
  //     onComplete({ error: 'PaymentElement not available' })
  //     updateGlobal({ createTokenRequest: null })
  //     return
  //   }

  //   console.log('[CONFIRMATION TOKEN] PaymentElement found, validating...')
  //   // Validate and create token
  //   elements.submit()
  //     .then(() => {
  //       console.log('[CONFIRMATION TOKEN] Validation passed, creating token...')
  //       return stripe.createConfirmationToken({
  //         elements: elements,
  //         params: {
  //           return_url: process.env.GATSBY_RETURN_URL,
  //         },
  //       })
  //     })
  //     .then((result) => {
  //       console.log('[CONFIRMATION TOKEN] Token creation result:', result)
  //       if (!result) {
  //         console.error('[CONFIRMATION TOKEN] No result from createConfirmationToken')
  //         onComplete({ error: 'Failed to create payment token - no response' })
  //         updateGlobal({ createTokenRequest: null })
  //         return
  //       }

  //       const { confirmationToken, error } = result
  //       if (error || !confirmationToken?.id) {
  //         console.error('[CONFIRMATION TOKEN] Error or no token ID:', { error, hasToken: !!confirmationToken?.id })
  //         onComplete({ error: error?.message || error?.code || 'Failed to create payment token' })
  //       } else {
  //         console.log('[CONFIRMATION TOKEN] Success! Token ID:', confirmationToken.id)
  //         onComplete({ confirmationToken: confirmationToken.id })
  //       }
  //       updateGlobal({ createTokenRequest: null })
  //     })
  //     .catch((err) => {
  //       console.error('[CONFIRMATION TOKEN] Exception:', err)
  //       onComplete({ error: err?.message || err?.code || 'Payment form validation failed' })
  //       updateGlobal({ createTokenRequest: null })
  //     })
  // }, [createTokenRequest, stripe, elements, updateGlobal])

  return null
}

const Checkout = ({ globalState }) => {
  const { store, updateGlobal } = globalState
  const { calculating, pd, ts, os, ss, orderID, stripe, preparePayment, showPayment, addressRequired, marketing, shipping_address, contactNumber, fullName, emailAddress, stripeForConfirmation, elementsForConfirmation, createTokenRequest, cartError, expressCartData, checkoutOpen } = store

  const fetchCart = useDispatch('fetchCart')
  const isOnline = useOnlineStatus();
  const [processing, setProcessing] = useState(false)
  const [isReady, setIsReady] = useState(false)
  // Rely entirely on Stripe's AddressElement to tell us if address is complete
  const [addressComplete, setAddressComplete] = useState(false)
  const [countryCode, setCountryCode] = useState(shipping_address?.country_code)
  // Track if address/email has changed since last fetchCart to determine button state
  const [hasChanges, setHasChanges] = useState(false)
  // Track the actual email value from LinkAuthenticationElement
  const [linkElementEmail, setLinkElementEmail] = useState('')
  // Debounce timer for email updates to prevent flashing on every keystroke
  const emailUpdateTimerRef = useRef(null)

  function setPreparePayment(preparePayment) {
    updateGlobal({ preparePayment: preparePayment })
  }

  const splitView = useBreakpointValue([false, false, true, true])
  const setAddress = useDispatch('setAddress')

  // Memoize stripe options to prevent Elements from remounting
  const memoizedStripeOptions = useMemo(() => {
    if (!pd?.cs) return null;
    return {
      clientSecret: pd.cs,
      appearance: appearance
    };
  }, [pd?.cs]);

  // Memoize fonts array to prevent Elements from remounting
  const stripeFonts = useMemo(() => {
    return [
      {family: 'ApercuLight', src: 'url(https://ingmarson.com/fonts/apercu-light.woff)', weight: '300'}
    ];
  }, []);

  // Sync email from cart to store
  useEffect(() => {
    if (ts?.email && ts.email !== emailAddress) {
      updateGlobal({ emailAddress: ts.email })
    }
  }, [ts?.email, emailAddress, updateGlobal])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (emailUpdateTimerRef.current) {
        clearTimeout(emailUpdateTimerRef.current)
      }
    }
  }, [])

  // Memoize AddressElement options - use ref to prevent flickering during calculation
  const lastStableAddress = useRef(null);
  const addressElementOptions = useMemo(() => {
    if (!shipping_address) return null;
    if (calculating && lastStableAddress.current) return lastStableAddress.current;

    const options = {
      defaultValues: {
        name: fullName,
        phone: contactNumber,
        address: {
          line1: shipping_address.address1,
          line2: shipping_address.address2,
          city: shipping_address.city,
          state: shipping_address.locality,
          postal_code: shipping_address.zip,
          country: shipping_address.country_code
        }
      },
      mode: 'shipping',
      fields: { phone: 'always' },
      validation: { phone: { required: 'always' } }
    };

    if (!calculating) lastStableAddress.current = options;
    return options;
  }, [fullName, contactNumber, shipping_address, calculating]);

  // NOTE: preparePayment is handled in pressButton - when user clicks "CONTINUE TO PAYMENT"
  // it sets preparePayment: true and calls fetchCart(true) directly
  // The cart fetch in network.js will check preparePayment and show payment if conditions are met
  // We don't need this useEffect anymore - it was causing preparePayment to reset too early

  // When address or email changes, go back to shipping page
  useEffect(() => {
    if (hasChanges && showPayment) {
      console.log('[CHECKOUT] Address/email changed - resetting to shipping page')
      updateGlobal({
        showPayment: false,
        showShipping: true,
        accordianItem: 0,
        preparePayment: false
      })
    }
  }, [hasChanges, showPayment, updateGlobal])

  // Reset hasChanges when payment intent is ready
  useEffect(() => {
    if (pd?.pi && !calculating && !processing) {
      const timer = setTimeout(() => setHasChanges(false), 100)
      return () => clearTimeout(timer)
    }
  }, [pd?.pi, calculating, processing]);

  // Removed changeAccordian - single page checkout doesn't need navigation


  // Helper to check if we have a valid email (MUST ALWAYS HAVE EMAIL IN LINK ELEMENT)
  const hasValidEmail = useMemo(() => {
    const emailFromLink = linkElementEmail?.trim() || ''
    return emailFromLink && emailFromLink.length > 0 && emailFromLink.includes('@')
  }, [linkElementEmail])

  // Memoize button message to avoid recalculating on every render
  // Show "PAY NOW" only when on payment step (showPayment is true)
  // Otherwise show "CONTINUE TO PAYMENT" when on shipping step
  const buttonMessage = useMemo(() => {
    if (calculating || processing) return ""
    // Only show "PAY NOW" if we're on the payment step AND all conditions are met
    if (showPayment && pd?.pi && addressComplete && hasValidEmail && !hasChanges) {
      return "PAY NOW"
    }
    // Otherwise show "CONTINUE TO PAYMENT" (we're on shipping step)
    return "CONTINUE TO PAYMENT"
  }, [calculating, processing, showPayment, pd?.pi, addressComplete, hasValidEmail, hasChanges])

  // Track if button press is in progress to prevent double taps on iOS
  const isButtonProcessing = useRef(false)

  const pressButton = useCallback(async (e) => {
    // Prevent default and stop propagation for iOS
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    // Prevent double taps on iOS
    if (isButtonProcessing.current) {
      console.log('[BUTTON] Already processing, ignoring tap')
      return
    }

    if (!hasValidEmail) {
      const errorMessage = 'Please select or enter an email address in the contact details field'
        updateGlobal({ cartError: errorMessage })
        return
      }

    if (buttonMessage === "CONTINUE TO PAYMENT") {
      isButtonProcessing.current = true
      setHasChanges(false)
      // Set preparePayment to trigger cart fetch that will show payment step
      // This ensures we always go through: Cart -> Confirm Shipping -> Pay
      updateGlobal({ preparePayment: true, cartError: "" })
      fetchCart(true)
      // Reset after a short delay to allow the state update
      setTimeout(() => {
        isButtonProcessing.current = false
      }, 500)
        return
      }

    if (buttonMessage === "PAY NOW") {
      const orderId = ss?.oid || orderID
      if (!stripeForConfirmation || !elementsForConfirmation || !pd?.pi || !orderId || !isReady) {
        updateGlobal({ cartError: "Checkout is not ready. Please wait a moment and try again." })
        return
      }

      // CRITICAL: Disable button immediately to prevent double clicks
      isButtonProcessing.current = true
      setProcessing(true)

      // TEMPORARILY: Use direct stripe.confirmPayment() instead of confirmation token
      // This bypasses the backend and tests 3D Secure directly
      const handleDirectPayment = async () => {
        try {
          // CRITICAL: Must call elements.submit() before confirmPayment()
          console.log('[PAYMENT] Validating elements before payment...')
          await elementsForConfirmation.submit()

          const returnURL = process.env.GATSBY_RETURN_URL
          const receiptEmail = ts?.email || emailAddress

          const confirmParams = {
            return_url: returnURL
          }

          // if (receiptEmail) {
          //   confirmParams.receipt_email = receiptEmail
          // }

          console.log('[PAYMENT] Using direct stripe.confirmPayment() (confirmation token disabled)')
          const result = await stripeForConfirmation.confirmPayment({
            elements: elementsForConfirmation,
            clientSecret: pd.cs,
            confirmParams: confirmParams,
          })

          // Handle 3D Secure redirect
          if (result?.paymentIntent?.status === 'requires_action' &&
              result?.paymentIntent?.next_action?.type === 'redirect_to_url') {
            console.log('[PAYMENT] 3D Secure redirect required:', result.paymentIntent.next_action.redirect_to_url.url)
            // Stripe should handle redirect automatically, but if not, redirect manually
            if (result.paymentIntent.next_action.redirect_to_url?.url) {
              window.location.href = result.paymentIntent.next_action.redirect_to_url.url
              return // Don't continue - redirect is happening
            }
          }

          if (result?.error) {
            const errorMsg = typeof result.error === 'string'
              ? result.error
              : result.error?.message || "Payment failed. Please try again."
            updateGlobal({ cartError: errorMsg })
            setProcessing(false)
            isButtonProcessing.current = false
            return
          }

          // Payment confirmed successfully
          updateGlobal({ cartError: "" })
          navigate(`/payment?payment_intent=${result.paymentIntent?.id || pd.pi}&order_id=${orderId}`)
        } catch (err) {
          console.error('[PAYMENT] Direct payment error:', err)
          updateGlobal({ cartError: err?.message || "Payment processing failed. Please try again." })
          setProcessing(false)
          isButtonProcessing.current = false
        }
      }

      handleDirectPayment()
      return

      // ORIGINAL CODE WITH CONFIRMATION TOKEN (TEMPORARILY DISABLED):
      // return new Promise((resolve) => {
      //   updateGlobal({
      //     createTokenRequest: {
      //       orderId,
      //       paymentIntentId: pd.pi,
      //       onComplete: async (result) => {
      //         if (result.error) {
      //           updateGlobal({ cartError: result.error })
      //           setProcessing(false)
      //           resolve()
      //           return
      //         }

      //         // Processing already set to true above - no need to set again

      //         try {
      //           const paymentResult = await confirmPayment(result.confirmationToken, pd.pi, orderId)
      //           console.log("PAYMENT RESULT", paymentResult)

      //           // Handle 3D Secure redirect
      //           if (paymentResult.requires_action && paymentResult.redirect_url) {
      //             console.log('[PAYMENT] Redirecting to 3D Secure:', paymentResult.redirect_url)
      //             // Redirect to 3D Secure authentication page
      //             window.location.href = paymentResult.redirect_url
      //             // Don't resolve - let the redirect happen
      //             navigate(paymentResult.redirect_url)
      //           }

      //           if (paymentResult.error) {
      //             const errorMsg = typeof paymentResult.error === 'string'
      //               ? paymentResult.error
      //               : paymentResult.error?.message || "Payment failed. Please try again."
      //             updateGlobal({ cartError: errorMsg })
      //             setProcessing(false)
      //             resolve()
      //             return
      //           }

      //           if (paymentResult.success) {
      //             updateGlobal({ cartError: "" })
      //             navigate(`/payment?payment_intent=${paymentResult.payment_intent_id || pd.pi}&order_id=${orderId}`)
      //           }
      //         } catch (err) {
      //           updateGlobal({ cartError: err?.response?.data?.error || err?.message || "Payment processing failed. Please try again." })
      //           setProcessing(false)
      //           isButtonProcessing.current = false
      //         }
      //         resolve()
      //       }
      //     }
      //   })
      // })
      // return
    }

    isButtonProcessing.current = true
    setHasChanges(false)
    fetchCart(true)
    setTimeout(() => {
      isButtonProcessing.current = false
    }, 500)
  }, [hasValidEmail, buttonMessage, stripeForConfirmation, elementsForConfirmation, pd?.pi, ss?.oid, orderID, isReady, updateGlobal, fetchCart])


  return (
    <Box minHeight={["80vh","80vh","00vh","90vh","90vh"]} display="flex" flexDirection="column">
      {/* Show checkout when main cart is ready - express cart will load separately */}
      {/* Express checkout will show immediately if we have express payment intent from main cart */}
      {(() => {
        const hasMainCart = pd && pd.cs;
        const hasStripe = stripe && memoizedStripeOptions;
        // Check for express payment intent from main cart OR separate express cart fetch
        const hasExpressCart = expressCartData?.expressPd?.cs;
        const mainCartReady = hasMainCart && hasStripe;

        // Log what we're waiting for
        if (!mainCartReady && checkoutOpen) {
          console.log('[CHECKOUT LOADING] Waiting for main cart:', {
            hasMainCart,
            hasStripe,
            hasExpressCart,
            expressCartDataExists: !!expressCartData,
            expressPdExists: !!expressCartData?.expressPd,
            expressPdCsExists: !!expressCartData?.expressPd?.cs,
            pdCs: pd?.cs ? `${pd.cs.substring(0, 20)}...` : 'none',
            calculating: calculating
          });
        } else if (mainCartReady && !hasExpressCart && checkoutOpen) {
          console.log('[CHECKOUT LOADING] Main cart ready, express payment intent will be available from main cart or will fetch separately');
        }

        // Allow checkout to show when main cart is ready
        // Express payment intent comes from main cart response, so it's available immediately
        return mainCartReady;
      })() ? (
        <Elements
          fonts={stripeFonts}
          options={memoizedStripeOptions}
          stripe={stripe}
          loader="auto"
        >
          <CheckoutContent globalState={globalState} />
          <Flex direction={'column'} flex="1" height={["100dvh","92dvh","90dvh","90dvh","90dvh"]} maxHeight={["100dvh","92dvh","90dvh","90dvh","90dvh"]} position="relative" overflowY={['auto', 'auto', 'hidden', 'hidden', 'hidden']}>
            <Box
              position={['sticky', 'sticky', 'relative', 'relative', 'relative']}
              top={[0, 0, 'auto', 'auto', 'auto']}
              zIndex={[20, 20, 'auto', 'auto', 'auto']}
              bg="white"
              py={4}
              flexShrink={0}
            >
              <Center>
                <Box id="ExpressCheckout" width="95%" maxW={'600px'} px={4}>
                  <VStack width="100%" spacing={4}>
                    <Box width="100%">
                      <div id="express-checkout-element" style={{ opacity: (calculating || processing) ? 0.5 : 1, pointerEvents: (calculating || processing) ? 'none' : 'auto' }}>
                        <ExpressPayment globalState={globalState} disabled={calculating || processing} processing={processing} setProcessing={setProcessing}/>
                      </div>
                    </Box>
                  </VStack>
                </Box>
              </Center>
            </Box>

            <Flex
              id="MainCheckout"
              direction="column"
              flex="1"
              minHeight="0"
              overflowY={['unset', 'unset', 'auto', 'auto', 'auto']}
            >
              <Box flex="1">
                <Center>
                  <Box id="checkout" width="95%" maxW={'600px'} >
                    <Box >
                      {pd?.pi && showPayment && (
                        <>
                          {cartError && (
                            <>
                              <Divider mb={2} borderColor="blackAlpha.500" />
                              <CartError />
                            </>
                          )}
                          {(processing || buttonMessage === "PAY NOW") && (
                            <Divider mb={0} borderColor="blackAlpha.500" />
                          )}
                          {/* Always render PaymentElement - hide with CSS to keep it mounted */}
                          <Box mt={0} mb={3} id="payment" border={'ActiveBorder'} style={{
                            visibility: showPayment ? 'visible' : 'hidden',
                            position: showPayment ? 'relative' : 'absolute',
                            height: showPayment ? 'auto' : '0',
                            overflow: 'hidden',
                            width: showPayment ? '100%' : '0',
                            opacity: showPayment ? 1 : 0
                          }}>
                            <PaymentForm globalState={globalState} setProcessing={setProcessing} setIsReady={setIsReady}/>
                          </Box>
                          <Divider mt={0} mb={5} borderColor="blackAlpha.500" />
                        </>
                      )}
                      <Box height="100%" marginLeft={'1px'} style={{ display: processing ? 'none' : 'block' }}>
                        <Flex mt={2} mb={2}>
                          <Text mt={0} variant={'checkout_header'}>CONTACT DETAILS</Text>
                        </Flex>
                        {/* Always show email field - no read-only mode */}
                        <Box mb={0}>
                          <LinkAuthenticationElement
                            key="link-auth-stable"
                            options={{
                              defaultValues: {
                                email: ts?.email || emailAddress || ''
                              }
                            }}
                            onChange={(event) => {
                              const email = event.value?.email || ''
                              const savedAccounts = event.savedAccounts || []

                              console.log('[LINK EMAIL DEBUG] onChange fired:', {
                                emailFromEvent: email,
                                currentEmailAddress: emailAddress,
                                savedAccounts: savedAccounts.map(acc => acc.email),
                                hasSavedAccounts: savedAccounts.length > 0
                              })

                              // If email is empty (user selected "USE ANOTHER EMAIL ADDRESS"), preserve store email
                              const emailToUse = email || (emailAddress || ts?.email || '')

                              setLinkElementEmail(emailToUse)

                              // Immediately store email in ref for cart fetches (updateGlobal is async)
                              setLatestEmailFromLink(emailToUse || null)

                              // Debounce global state updates to prevent flashing on every keystroke
                              // Clear any existing timer
                              if (emailUpdateTimerRef.current) {
                                clearTimeout(emailUpdateTimerRef.current)
                              }

                              // Batch updates (excluding email which is debounced separately)
                              const updates = {}

                              if (emailToUse && emailToUse !== emailAddress) {
                                // Debounce the update - only update after user stops typing for 500ms
                                // This prevents the page from flashing on every keystroke
                                emailUpdateTimerRef.current = setTimeout(() => {
                                  console.log('[LINK EMAIL DEBUG] Debounced update: emailAddress from', emailAddress, 'to', emailToUse)
                                  updateGlobal({ emailAddress: emailToUse })
                                }, 500)
                              }

                              // Handle address from Link
                              if (event.value.billingDetails?.address) {
                                const addr = event.value.billingDetails.address
                                updates.addressFromLink = true
                                setAddress({
                                  addressType: 'shipping_address',
                                  address: {
                                    country_code: addr.country,
                                    zip: addr.postal_code,
                                    address1: addr.line1 || '',
                                    address2: addr.line2 || '',
                                    city: addr.city || '',
                                    province: addr.state || '',
                                    name: event.value.billingDetails.name || ''
                                  },
                                  otherData: {
                                    fullName: event.value.billingDetails.name || fullName,
                                    contactNumber: event.value.billingDetails.phone || contactNumber,
                                    showShipping: true,
                                    calculating: false
                                  }
                                })
                              } else {
                                updates.addressFromLink = false
                              }

                              if (Object.keys(updates).length > 0) {
                                updateGlobal(updates)
                                setHasChanges(true)
                              }
                            }}
                          />
                        </Box>
                        {addressRequired &&
                          <>
                            <Flex mt={5} mb={2}>
                              <Text variant={'checkout_header'}>SHIPPING ADDRESS</Text>
                            </Flex>
                            {/* Always render AddressElement - hide with CSS to keep it mounted when processing */}
                            {addressElementOptions && (
                              <Box style={{
                                visibility: processing ? 'hidden' : 'visible',
                                position: processing ? 'absolute' : 'relative',
                                height: processing ? '0' : 'auto',
                                overflow: 'hidden',
                                width: processing ? '0' : '100%'
                              }}>
                                <AddressElement
                                  options={addressElementOptions}
                                  onChange={(event) => {
                                    setAddressComplete(event.complete)
                                    setHasChanges(true)

                                    if (event.complete) {
                                      const data = event.value
                                      const addr = data.address
                                      const countryChanged = addr.country !== shipping_address?.country_code

                                      if (countryChanged) {
                                        setCountryCode(addr.country)
                                      }

                                      updateGlobal({ addressFromLink: false })

                                      setAddress({
                                        addressType: 'shipping_address',
                                        address: {
                                          country_code: addr.country,
                                          zip: addr.postal_code,
                                          address1: addr.line1 || '',
                                          address2: addr.line2 || '',
                                          city: addr.city || '',
                                          province: addr.state || '',
                                          name: data.name || fullName || ''
                                        },
                                        otherData: {
                                          fullName: data.name || fullName,
                                          contactNumber: data.phone || contactNumber,
                                          showShipping: true,
                                          calculating: false,
                                          countryChange: countryChanged,
                                          shippingID: null
                                        }
                                      })
                                    }
                                  }}
                                />
                              </Box>
                            )}
                          </>
                        }
                      </Box>
                      {processing && (
                        <Flex direction={'column'} flex="1" maxHeight={["100dvh","92dvh","95dvh","95dvh","95dvh"]} position="absolute" top={0} left={0} right={0} bottom={0} bg="white" zIndex={10}>
                          <Box flex="1" display="flex" alignItems="center" justifyContent="center">
                            <VStack spacing={4}>
                              <Box>
                                <Dots backgroundColor="#000" />
                              </Box>
                              <Text
                                fontSize="14px"
                                fontWeight="300"
                                textTransform="uppercase"
                                letterSpacing="1px"
                                color="gray.600"
                              >
                                Processing payment
                              </Text>
                            </VStack>
                          </Box>
                        </Flex>
                      )}
                    </Box>
                  </Box>
                </Center>
              </Box>
              {!processing && (
                <Box
                  position="sticky"
                  bottom={0}
                  bg="white"
                  pt={splitView ? 0 : 4}
                  pb={splitView ? 0 : 4}
                  zIndex={10}
                  flexShrink={0}
                  width="100%"
                >
                  <Center>
                    <Box
                      id="shoppingCartBottom"
                      width="95%"
                      maxW={'600px'}
                      mr={splitView ? 5 : 0}
                      ml={splitView ? 5 : 0}
                      mt={splitView ? 2 : 0}
                      px={splitView ? 0 : 4}
                    >
                      {/* Only show checkbox if marketing hasn't been set to subscribed (os.ms !== true) */}
                      {os && os.ms !== true && (
                        <Checkbox
                          isChecked={marketing}
                          onChange={(e) => updateGlobal({ marketing: e.target.checked, accepts_marketing: true })}
                        >
                          RECEIVE NEWSLETTER / PROMOTIONS
                        </Checkbox>
                      )}
                      <Box>
                        {/* <CartUpsells globalState={globalState} /> */}
                        {!splitView &&
                          <ShoppingCartTotals globalState={globalState} />
                        }
                        <Button
                          variant={'checkout'}
                          mb={[5,5,0,0]}
                          type={'button'}
                          isDisabled={
                            !isOnline ||
                            processing ||
                            calculating ||
                            (buttonMessage === "PAY NOW" && pd?.pi && !isReady) ||
                            (addressRequired && !addressComplete) ||
                            !hasValidEmail
                          }
                          rightIcon={processing || calculating ? <Dots /> : <ArrowForwardIcon />}
                          onClick={pressButton}
                          onTouchStart={(e) => {
                            // iOS touch handling - prevent double tap
                            e.stopPropagation()
                          }}
                          style={{
                            WebkitTapHighlightColor: 'transparent',
                            touchAction: 'manipulation',
                            userSelect: 'none'
                          }}
                        >
                          {buttonMessage}
                        </Button>
                      </Box>
                    </Box>
                  </Center>
                </Box>
              )}
            </Flex>
          </Flex>
        </Elements>
      ) : (
        <Flex direction={'column'} flex="1" maxHeight={["100dvh","92dvh","95dvh","95dvh","95dvh"]}>
          <Box flex="1" display="flex" alignItems="center" justifyContent="center">
            <VStack spacing={4}>
              <Box>
                <Dots backgroundColor="#000" />
              </Box>
              <Text
                fontSize="14px"
                fontWeight="300"
                textTransform="uppercase"
                letterSpacing="1px"
                color="gray.600"
              >
                Preparing your checkout
              </Text>
            </VStack>
          </Box>
        </Flex>
      )}
    </Box>
  )
}

export default Checkout